import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"

const textareaVariants = cva(
  "border-input placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex w-full rounded-md border-[1.5px] bg-transparent font-black transition-[color,box-shadow,border-radius] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "min-h-20 rounded-[14px] px-4 py-2.5 text-sm",
        md: "min-h-24 rounded-[18px] px-5 py-3 text-base md:text-sm",
        lg: "min-h-32 rounded-[24px] px-6 py-4 text-base",
      },
      resize: {
        none: "resize-none",
        vertical: "resize-y",
        horizontal: "resize-x",
        both: "resize",
        auto: "resize-none field-sizing-content",
      },
    },
    defaultVariants: {
      size: "md",
      resize: "auto",
    },
  }
)

export interface TextareaProps
  extends Omit<React.ComponentProps<"textarea">, "size">,
    VariantProps<typeof textareaVariants> {
  /**
   * If true, the textarea will take up the full width of its container
   */
  fullWidth?: boolean
}

function Textarea({
  className,
  size,
  resize,
  fullWidth = true,
  ...props
}: TextareaProps) {
  return (
    <textarea
      data-slot="textarea"
      data-size={size}
      data-resize={resize}
      className={cn(
        textareaVariants({ size, resize }),
        !fullWidth && "w-auto",
        className
      )}
      {...props}
    />
  )
}

Textarea.displayName = "Textarea"

export { Textarea }
